// External packages
import * as React from 'react';

// Components
import { Select, Option } from 'components/Select';
import { Checkbox } from 'components/Checkbox';
import { Icon } from 'components/Icons';
import { PlainButton } from 'components/Button';

// Modules
import { BulkActionsToolbar } from 'modules/BulkActionsToolbar';
import { ImageGalleryItem } from 'modules/images/ImageGalleryItem';

// Hooks
import { useDeleteMedia, useGetAllMedia } from 'api/media';
import { useBulkSelection } from 'hooks/useBulkSelection';

// Types
import type { Image } from '../../../../server/src/types/media.types';

// Utils
import { sortMedia } from 'utils/sortMedia';
import { getBestImageUrl } from 'utils/getImageUrl';

export const ImageGallery = () => {
  const [sortBy, setSortBy] = React.useState<string>('datum');
  const [modalOpen, setModalOpen] = React.useState(false);

  const { data: media, isLoading, isError } = useGetAllMedia('images');
  const { mutate: deleteMedia, isPending: isDeleting } = useDeleteMedia();

  const sortedMedia = React.useMemo(
    () => sortMedia(media, sortBy),
    [media, sortBy]
  ) as Image[];

  const bulkSelection = useBulkSelection(sortedMedia);

  const handleBulkDelete = () => {
    bulkSelection.selectedIds.forEach((id) => {
      deleteMedia({ fileType: 'images', id });
    });
    bulkSelection.clearSelection();
    setModalOpen(false);
  };

  return (
    <>
      {sortedMedia && sortedMedia.length > 0 && (
        <>
          <Select
            placeholder="Sortiraj po"
            wrapperClassName="ml-auto"
            className="w-auto"
            selectedKey={sortBy}
            onSelectionChange={(key) => setSortBy(key as string)}
          >
            <Option id="datum">Dodano (najnovije)</Option>
            <Option id="datum-desc">Dodano (najstarije)</Option>
          </Select>
          <BulkActionsToolbar
            allSelected={!!bulkSelection.allSelected}
            someSelected={!!bulkSelection.someSelected}
            selectedCount={bulkSelection.selectedIds.length}
            onSelectAll={bulkSelection.handleSelectAll}
            onBulkDelete={handleBulkDelete}
            isDeleteModalOpen={modalOpen}
            setDeleteModalOpen={setModalOpen}
            isDeleting={isDeleting}
            label="Odaberi sve"
          />
        </>
      )}

      {isLoading || isError || !sortedMedia || sortedMedia.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {isLoading ? (
            <Icon name="loader" className="animate-spin size-10" />
          ) : isError ? (
            <p>Greška pri učitavanju slika.</p>
          ) : (
            <p>Nema pronađenih slika.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 overflow-x-auto rounded-lg border border-gray-200 bg-white p-4 lg:grid-cols-4">
          {sortedMedia.map((image) => (
            <div key={image.id}>
              <Checkbox
                isSelected={bulkSelection.selectedIds.includes(image.id)}
                onChange={(checked) =>
                  bulkSelection.handleSelect(image.id, !!checked)
                }
              />
              <ImageGalleryItem
                item={image}
                modalTrigger={
                  <PlainButton className="cursor-pointer rounded-lg">
                    <div className="overflow-hidden group aspect-image rounded-lg">
                      <img
                        className="h-full w-full object-cover transition-transform group-hover:scale-110"
                        src={getBestImageUrl(image, 'medium')}
                        alt={image.altText || ''}
                      />
                    </div>
                  </PlainButton>
                }
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};
